import { useEffect, useRef, useCallback } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  opacity: number;
  pulseSpeed: number;
  pulseOffset: number;
}

interface AnimatedBackgroundProps {
  mode?: 'particles' | 'grid' | 'data-stream' | 'orbs';
  density?: number;
  accentColor?: string;
  className?: string;
}

export default function AnimatedBackground({
  mode = 'particles',
  density = 80,
  className = '',
}: AnimatedBackgroundProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const mouseRef = useRef({ x: -1000, y: -1000 });
  const particlesRef = useRef<Particle[]>([]);
  const animFrameRef = useRef<number>(0);
  const timeRef = useRef(0);

  const initParticles = useCallback(
    (w: number, h: number) => {
      const count = Math.min(density, Math.floor((w * h) / 8000));
      const particles: Particle[] = [];
      for (let i = 0; i < count; i++) {
        particles.push({
          x: Math.random() * w,
          y: Math.random() * h,
          vx: (Math.random() - 0.5) * 0.4,
          vy: (Math.random() - 0.5) * 0.4,
          size: Math.random() * 1.8 + 0.5,
          opacity: Math.random() * 0.5 + 0.15,
          pulseSpeed: Math.random() * 0.02 + 0.005,
          pulseOffset: Math.random() * Math.PI * 2,
        });
      }
      particlesRef.current = particles;
    },
    [density],
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      const dpr = window.devicePixelRatio || 1;
      const rect = canvas.parentElement?.getBoundingClientRect();
      if (!rect) return;
      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;
      canvas.style.width = `${rect.width}px`;
      canvas.style.height = `${rect.height}px`;
      ctx.scale(dpr, dpr);
    };

    resize();
    const w = canvas.width / (window.devicePixelRatio || 1);
    const h = canvas.height / (window.devicePixelRatio || 1);
    initParticles(w, h);

    const handleMouse = (e: MouseEvent) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = {
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      };
    };

    window.addEventListener('resize', resize);
    canvas.parentElement?.addEventListener('mousemove', handleMouse);

    const drawGrid = () => {
      const cw = canvas.width / (window.devicePixelRatio || 1);
      const ch = canvas.height / (window.devicePixelRatio || 1);
      const gridSize = 50;
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      ctx.clearRect(0, 0, cw, ch);
      ctx.strokeStyle = 'rgba(195, 155, 105, 0.06)';
      ctx.lineWidth = 0.5;

      for (let x = 0; x < cw; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, ch);
        ctx.stroke();
      }
      for (let y = 0; y < ch; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(cw, y);
        ctx.stroke();
      }

      // Mouse glow on grid
      const gradient = ctx.createRadialGradient(mx, my, 0, mx, my, 200);
      gradient.addColorStop(0, 'rgba(195, 155, 105, 0.08)');
      gradient.addColorStop(1, 'rgba(195, 155, 105, 0)');
      ctx.fillStyle = gradient;
      ctx.fillRect(0, 0, cw, ch);
    };

    const drawParticles = () => {
      const cw = canvas.width / (window.devicePixelRatio || 1);
      const ch = canvas.height / (window.devicePixelRatio || 1);
      const mx = mouseRef.current.x;
      const my = mouseRef.current.y;

      ctx.clearRect(0, 0, cw, ch);
      const particles = particlesRef.current;

      // Draw connections
      ctx.strokeStyle = 'rgba(195, 155, 105, 0.05)';
      ctx.lineWidth = 0.5;
      for (let i = 0; i < particles.length; i++) {
        for (let j = i + 1; j < particles.length; j++) {
          const dx = particles[i].x - particles[j].x;
          const dy = particles[i].y - particles[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 120) {
            ctx.beginPath();
            ctx.moveTo(particles[i].x, particles[i].y);
            ctx.lineTo(particles[j].x, particles[j].y);
            ctx.stroke();
          }
        }
      }

      // Draw particles
      for (const p of particles) {
        // Mouse attraction
        const dx = mx - p.x;
        const dy = my - p.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 200 && dist > 0) {
          p.vx += (dx / dist) * 0.02;
          p.vy += (dy / dist) * 0.02;
        }

        p.x += p.vx;
        p.y += p.vy;
        p.vx *= 0.999;
        p.vy *= 0.999;

        // Bounce
        if (p.x < 0) { p.x = 0; p.vx *= -1; }
        if (p.x > cw) { p.x = cw; p.vx *= -1; }
        if (p.y < 0) { p.y = 0; p.vy *= -1; }
        if (p.y > ch) { p.y = ch; p.vy *= -1; }

        const pulse = Math.sin(timeRef.current * p.pulseSpeed + p.pulseOffset) * 0.3 + 0.7;
        const alpha = p.opacity * pulse;

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(195, 155, 105, ${alpha})`;
        ctx.fill();

        // Glow halo
        if (dist < 200) {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size * 2.5, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(195, 155, 105, ${alpha * 0.2})`;
          ctx.fill();
        }
      }
    };

    const drawDataStream = () => {
      const cw = canvas.width / (window.devicePixelRatio || 1);
      const ch = canvas.height / (window.devicePixelRatio || 1);

      ctx.fillStyle = 'rgba(12, 12, 16, 0.15)';
      ctx.fillRect(0, 0, cw, ch);

      const streamCount = Math.floor(cw / 60);
      for (let i = 0; i < streamCount; i++) {
        const x = (i * 60 + (timeRef.current * 20) % 60) % cw;
        const len = 40 + Math.sin(i + timeRef.current * 0.01) * 30;

        const gradient = ctx.createLinearGradient(x, 0, x, ch);
        gradient.addColorStop(0, 'rgba(195, 155, 105, 0)');
        gradient.addColorStop(0.4, 'rgba(195, 155, 105, 0.10)');
        gradient.addColorStop(0.5, 'rgba(195, 155, 105, 0.22)');
        gradient.addColorStop(0.6, 'rgba(195, 155, 105, 0.10)');
        gradient.addColorStop(1, 'rgba(195, 155, 105, 0)');

        ctx.fillStyle = gradient;
        ctx.fillRect(x - 0.5, 0, 1, ch);
      }
    };

    const drawOrbs = () => {
      const cw = canvas.width / (window.devicePixelRatio || 1);
      const ch = canvas.height / (window.devicePixelRatio || 1);

      ctx.clearRect(0, 0, cw, ch);

      const orbs = [
        { x: cw * 0.2, y: ch * 0.3, r: 180, speed: 0.3 },
        { x: cw * 0.7, y: ch * 0.5, r: 140, speed: 0.5 },
        { x: cw * 0.5, y: ch * 0.75, r: 220, speed: 0.2 },
      ];

      for (const orb of orbs) {
        const ox = orb.x + Math.sin(timeRef.current * orb.speed * 0.001) * 40;
        const oy = orb.y + Math.cos(timeRef.current * orb.speed * 0.001) * 30;
        const pulse = Math.sin(timeRef.current * 0.001 + orb.speed) * 0.2 + 0.8;

        const gradient = ctx.createRadialGradient(ox, oy, 0, ox, oy, orb.r * pulse);
        gradient.addColorStop(0, 'rgba(180, 140, 95, 0.10)');
        gradient.addColorStop(0.4, 'rgba(195, 155, 105, 0.04)');
        gradient.addColorStop(0.7, 'rgba(195, 155, 105, 0.01)');
        gradient.addColorStop(1, 'rgba(195, 155, 105, 0)');

        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, cw, ch);
      }
    };

    const drawFn =
      mode === 'grid'
        ? drawGrid
        : mode === 'data-stream'
          ? drawDataStream
          : mode === 'orbs'
            ? drawOrbs
            : drawParticles;

    const animate = () => {
      timeRef.current += 16;
      drawFn();
      animFrameRef.current = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resize);
      canvas.parentElement?.removeEventListener('mousemove', handleMouse);
      cancelAnimationFrame(animFrameRef.current);
    };
  }, [mode, initParticles]);

  return (
    <canvas
      ref={canvasRef}
      className={`absolute inset-0 pointer-events-none ${className}`}
    />
  );
}